import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import api from '../services/authService';
import { colors } from '../styles/colors';
import Pagination from './Pagination';
import OpportunityDetailsModal from './OpportunityDetailsModal';
import CompanyDetail from './CompanyDetail';
import CompanyForm from './CompanyForm';
import RecruiterDetailModal from './RecruiterDetailModal';
import RecruiterEditModal from './RecruiterEditModal';
import { Pagination as PaginationType } from '../types';

interface Opportunity {
  id: number;
  name: string;
  fancy_name?: string;
  status: number;
  published: boolean;
  applies_count: number;
  created_at: string;
  published_at?: string;
  expires_at?: string;
  recruiter_id?: number;
  recruiter_name?: string;
  recruiter_email?: string;
  company_id: number;
  company_name: string;
}

interface OpportunitiesProps {
  title?: string;
  endpoint?: string;
  emptyStateLabel?: string;
  filterPreset?: 'default' | 'integration';
}

const Opportunities: React.FC<OpportunitiesProps> = ({
  title = 'Oportunidades',
  endpoint = '/opportunities',
  emptyStateLabel = 'Nenhuma oportunidade encontrada',
  filterPreset = 'default',
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const skipNextFetch = useRef(false);
  const isInitialMount = useRef(true);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [pagination, setPagination] = useState<PaginationType>({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [publishedFilter, setPublishedFilter] = useState('');
  const [integrationScope, setIntegrationScope] = useState('moderating');
  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('name');
  const [viewingOpportunity, setViewingOpportunity] = useState<number | null>(null);
  const [viewingCompanyId, setViewingCompanyId] = useState<number | null>(null);
  const [editingCompanyId, setEditingCompanyId] = useState<number | null>(null);
  const [viewingRecruiterId, setViewingRecruiterId] = useState<number | null>(null);
  const [editingRecruiterId, setEditingRecruiterId] = useState<number | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);

  useEffect(() => {
    const opportunityId = searchParams.get('opportunityId');
    if (opportunityId) {
      skipNextFetch.current = true;
      setSearchParams({});
      setSearchType('id');
      setSearch(opportunityId);
      setSearchTerm(opportunityId);
      setPagination(prev => ({ ...prev, page: 1 }));
      
      const fetchDirect = async () => {
        try {
          setLoading(true);
          const params = new URLSearchParams({
            page: '1',
            limit: pagination.limit.toString(),
            search: opportunityId,
            searchType: 'id',
          });
          const response = await api.get(`${endpoint}?${params}`);
          setOpportunities(response.data.opportunities);
          setPagination(response.data.pagination);
        } catch (error: any) {
          if (error.name !== 'CanceledError') {
            toast.error('Erro ao carregar oportunidades');
          }
        } finally {
          setLoading(false);
        }
      };
      fetchDirect();
    }
  }, [location.search]);

  useEffect(() => {
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (!searchParams.get('opportunityId')) {
        fetchOpportunities();
      }
      return;
    }
    fetchOpportunities();
  }, [pagination.page, statusFilter, publishedFilter, integrationScope, searchTerm]);

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        ...(statusFilter && { status: statusFilter }),
        ...(publishedFilter && { published: publishedFilter }),
        ...(filterPreset === 'integration' && { scope: integrationScope }),
        ...(searchTerm && { search: searchTerm, searchType }),
      });

      const response = await api.get(`${endpoint}?${params}`);
      setOpportunities(response.data.opportunities);
      setPagination(response.data.pagination);
    } catch (error: any) {
      if (error.name !== 'CanceledError') {
        toast.error('Erro ao carregar oportunidades');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchTerm(search);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearchTypeChange = (newType: string) => {
    setSearchType(newType);
    setSearch('');
    setSearchTerm('');
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    setSearch('');
    setSearchTerm('');
    setSearchType('name');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePublishedFilter = (published: string) => {
    setPublishedFilter(published);
    setSearch('');
    setSearchTerm('');
    setSearchType('name');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleIntegrationScope = (scope: string) => {
    setIntegrationScope(scope);
    setSearch('');
    setSearchTerm('');
    setSearchType('name');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleEditOpportunity = (opportunityId: number) => {
    const basePath = filterPreset === 'integration' ? '/integration-opportunities' : '/opportunities';
    navigate(`${basePath}/${opportunityId}/edit`);
  };

  const handleViewOpportunity = (opportunityId: number) => {
    const basePath = filterPreset === 'integration' ? '/integration-opportunities' : '/opportunities';
    navigate(`${basePath}/${opportunityId}/details`);
  };

  const toggleDropdown = (key: string) => {
    setDropdownOpen(dropdownOpen === key ? null : key);
  };

  const handleViewCompany = (companyId: number) => {
    setViewingCompanyId(companyId);
  };

  const handleEditCompany = (companyId: number) => {
    setEditingCompanyId(companyId);
  };

  const handleViewRecruiter = (recruiterId?: number) => {
    if (!recruiterId) return;
    setViewingRecruiterId(recruiterId);
  };

  const handleEditRecruiter = (recruiterId?: number) => {
    if (!recruiterId) return;
    setEditingRecruiterId(recruiterId);
  };

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownOpen) {
        setDropdownOpen(null);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [dropdownOpen]);

  const getStatusLabel = (status: number) => {
    const statusMap: { [key: number]: string } = {
      0: 'Rascunho',
      1: 'Travada',
      2: 'Ativa',
      3: 'Desativada'
    };
    return statusMap[status] || 'Desconhecido';
  };

  const getStatusColor = (status: number) => {
    const colorMap: { [key: number]: string } = {
      0: colors.textLight,
      1: '#FFA500',
      2: colors.success,
      3: colors.error
    };
    return colorMap[status] || colors.textLight;
  };

  return (
    <div style={styles.container} onClick={() => setDropdownOpen(null)}>
      <div style={styles.header}>
        <h1 style={styles.title}>{title}</h1>
      </div>

      <div style={styles.scopes}>
        {filterPreset === 'integration' ? (
          <>
            {[
              ['all', 'All'],
              ['moderating', 'Moderando'],
              ['published', 'Publicadas'],
              ['unpublished', 'Despublicadas'],
              ['expired', 'Expiradas'],
              ['deleted', 'Excluídas'],
            ].map(([scope, label]) => (
              <button
                key={scope}
                onClick={() => handleIntegrationScope(scope)}
                style={{...styles.scopeBtn, ...(integrationScope === scope ? styles.scopeBtnActive : {})}}
              >
                {label}
              </button>
            ))}
          </>
        ) : (
          <>
            <button 
              onClick={() => handleStatusFilter('')} 
              style={{...styles.scopeBtn, ...(statusFilter === '' ? styles.scopeBtnActive : {})}}
            >
              Todos
            </button>
            <button 
              onClick={() => handleStatusFilter('0')} 
              style={{...styles.scopeBtn, ...(statusFilter === '0' ? styles.scopeBtnActive : {})}}
            >
              Rascunho
            </button>
            <button 
              onClick={() => handleStatusFilter('1')} 
              style={{...styles.scopeBtn, ...(statusFilter === '1' ? styles.scopeBtnActive : {})}}
            >
              Travada
            </button>
            <button 
              onClick={() => handleStatusFilter('2')} 
              style={{...styles.scopeBtn, ...(statusFilter === '2' ? styles.scopeBtnActive : {})}}
            >
              Ativa
            </button>
            <button 
              onClick={() => handleStatusFilter('3')} 
              style={{...styles.scopeBtn, ...(statusFilter === '3' ? styles.scopeBtnActive : {})}}
            >
              Desativada
            </button>
            <button 
              onClick={() => handleStatusFilter('moderating')} 
              style={{...styles.scopeBtn, ...(statusFilter === 'moderating' ? styles.scopeBtnActive : {})}}
            >
              Em moderação
            </button>
            <button 
              onClick={() => handleStatusFilter('republished')} 
              style={{...styles.scopeBtn, ...(statusFilter === 'republished' ? styles.scopeBtnActive : {})}}
            >
              Republicadas
            </button>
            <button 
              onClick={() => handleStatusFilter('trials')} 
              style={{...styles.scopeBtn, ...(statusFilter === 'trials' ? styles.scopeBtnActive : {})}}
            >
              Trials
            </button>
            <button 
              onClick={() => handleStatusFilter('hunting')} 
              style={{...styles.scopeBtn, ...(statusFilter === 'hunting' ? styles.scopeBtnActive : {})}}
            >
              Hunting
            </button>
            <button 
              onClick={() => handleStatusFilter('not_paid')} 
              style={{...styles.scopeBtn, ...(statusFilter === 'not_paid' ? styles.scopeBtnActive : {})}}
            >
              Não pagas
            </button>
            <span style={styles.separator}>|</span>
            <button 
              onClick={() => handlePublishedFilter('')} 
              style={{...styles.scopeBtn, ...(publishedFilter === '' ? styles.scopeBtnActive : {})}}
            >
              Todas
            </button>
            <button 
              onClick={() => handlePublishedFilter('true')} 
              style={{...styles.scopeBtn, ...(publishedFilter === 'true' ? styles.scopeBtnActive : {})}}
            >
              Publicadas
            </button>
            <button 
              onClick={() => handlePublishedFilter('false')} 
              style={{...styles.scopeBtn, ...(publishedFilter === 'false' ? styles.scopeBtnActive : {})}}
            >
              Não Publicadas
            </button>
          </>
        )}
      </div>

      <form onSubmit={handleSearch} style={styles.filters}>
        <select
          value={searchType}
          onChange={(e) => handleSearchTypeChange(e.target.value)}
          style={styles.select}
        >
          <option value="name">Nome da vaga</option>
          <option value="id">ID</option>
          <option value="company">Empresa</option>
        </select>
        <input
          type="text"
          placeholder={`Buscar por ${searchType === 'name' ? 'nome da vaga' : searchType === 'id' ? 'ID' : 'empresa'}...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.input}
        />
        <button type="submit" style={styles.searchBtn}>
          Buscar
        </button>
      </form>

      {loading ? (
        <div style={styles.loading}>Carregando...</div>
      ) : (
        <>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={styles.th}>ID</th>
                  <th style={styles.th}>Nome</th>
                  <th style={styles.th}>Empresa</th>
                  <th style={styles.th}>Recrutador</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Publicada</th>
                  <th style={styles.th}>Candidaturas</th>
                  <th style={styles.th}>Criada em</th>
                  <th style={styles.th}>Publicada em</th>
                  <th style={styles.th}>Expira em</th>
                  <th style={styles.th}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {opportunities.map((opp) => (
                  <tr key={opp.id} style={styles.tableRow}>
                    <td style={styles.td}>{opp.id}</td>
                    <td style={styles.td}>{opp.fancy_name || opp.name}</td>
                    <td style={styles.td}>
                      <div style={styles.entityCell}>
                        <span>{opp.company_name}</span>
                        <div style={styles.dropdown}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(`company-${opp.id}`);
                            }}
                            style={styles.inlineActionBtn}
                            title="Ações da empresa"
                          >
                            ⋮
                          </button>
                          {dropdownOpen === `company-${opp.id}` && (
                            <div style={styles.dropdownMenu}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewCompany(opp.company_id);
                                  setDropdownOpen(null);
                                }}
                                style={styles.dropdownItem}
                              >
                                Visualizar empresa
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditCompany(opp.company_id);
                                  setDropdownOpen(null);
                                }}
                                style={styles.dropdownItem}
                              >
                                Editar empresa
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.entityCell}>
                        <span>{opp.recruiter_name || opp.recruiter_email || '-'}</span>
                        {opp.recruiter_id && (
                          <div style={styles.dropdown}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDropdown(`recruiter-${opp.id}`);
                              }}
                              style={styles.inlineActionBtn}
                              title="Ações do recrutador"
                            >
                              ⋮
                            </button>
                            {dropdownOpen === `recruiter-${opp.id}` && (
                              <div style={styles.dropdownMenu}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewRecruiter(opp.recruiter_id);
                                    setDropdownOpen(null);
                                  }}
                                  style={styles.dropdownItem}
                                >
                                  Visualizar recrutador
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditRecruiter(opp.recruiter_id);
                                    setDropdownOpen(null);
                                  }}
                                  style={styles.dropdownItem}
                                >
                                  Editar recrutador
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: getStatusColor(opp.status)
                      }}>
                        {getStatusLabel(opp.status)}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {opp.published ? (
                        <span style={{...styles.badge, backgroundColor: colors.success}}>SIM</span>
                      ) : (
                        <span style={{...styles.badge, backgroundColor: colors.error}}>NÃO</span>
                      )}
                    </td>
                    <td style={styles.td}>{opp.applies_count}</td>
                    <td style={styles.td}>
                      {new Date(opp.created_at).toLocaleDateString('pt-BR')}
                    </td>
                    <td style={styles.td}>
                      {opp.published_at ? new Date(opp.published_at).toLocaleDateString('pt-BR') : '-'}
                    </td>
                    <td style={styles.td}>
                      {opp.expires_at ? new Date(opp.expires_at).toLocaleDateString('pt-BR') : '-'}
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionsCell}>
                        <div style={styles.dropdown}>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(`opportunity-${opp.id}`);
                            }}
                            style={styles.actionBtn}
                            title="Ações"
                          >
                            ⋮
                          </button>
                          {dropdownOpen === `opportunity-${opp.id}` && (
                            <div style={styles.dropdownMenu}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewOpportunity(opp.id);
                                  setDropdownOpen(null);
                                }}
                                style={styles.dropdownItem}
                              >
                                Ver Detalhes
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditOpportunity(opp.id);
                                  setDropdownOpen(null);
                                }}
                                style={styles.dropdownItem}
                              >
                                Editar
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {opportunities.length === 0 && (
              <div style={styles.emptyState}>{emptyStateLabel}</div>
            )}
          </div>

          <div style={styles.paginationContainer}>
            <div style={styles.paginationInfo}>
              Página {pagination.page} de {pagination.totalPages} ({pagination.total} total)
            </div>
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}
      
      {viewingOpportunity && (
        <OpportunityDetailsModal
          opportunityId={viewingOpportunity}
          onClose={() => setViewingOpportunity(null)}
        />
      )}

      {viewingCompanyId && (
        <div style={styles.modalOverlay} onClick={() => setViewingCompanyId(null)}>
          <div style={styles.largeModal} onClick={(e) => e.stopPropagation()}>
            <CompanyDetail
              companyId={viewingCompanyId}
              onClose={() => setViewingCompanyId(null)}
            />
          </div>
        </div>
      )}

      {editingCompanyId && (
        <CompanyForm
          company={{ id: editingCompanyId } as any}
          onCancel={() => setEditingCompanyId(null)}
          onSave={() => {
            setEditingCompanyId(null);
            fetchOpportunities();
          }}
        />
      )}

      {viewingRecruiterId && (
        <RecruiterDetailModal
          recruiterId={viewingRecruiterId}
          onClose={() => setViewingRecruiterId(null)}
        />
      )}

      {editingRecruiterId && (
        <RecruiterEditModal
          recruiterId={editingRecruiterId}
          onClose={() => setEditingRecruiterId(null)}
          onSave={() => {
            setEditingRecruiterId(null);
            fetchOpportunities();
          }}
        />
      )}
    </div>
  );
};

const styles = {
  container: {
    width: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2rem',
    color: colors.primary,
    marginBottom: '2rem',
  },
  scopes: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
  },
  scopeBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: colors.white,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '0.9rem',
  },
  scopeBtnActive: {
    backgroundColor: colors.primary,
    color: colors.white,
    borderColor: colors.primary,
  },
  separator: {
    color: colors.textLight,
    fontSize: '1.2rem',
    margin: '0 0.5rem',
  },
  filters: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '2rem',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
  },
  select: {
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    minWidth: '120px',
    backgroundColor: colors.white,
  },
  input: {
    padding: '0.75rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '1rem',
    minWidth: '300px',
  },
  searchBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  tableContainer: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    overflow: 'visible' as const,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  tableHeader: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  th: {
    padding: '1rem',
    textAlign: 'left' as const,
    fontWeight: '600',
  },
  tableRow: {
    borderBottom: `1px solid ${colors.border}`,
  },
  td: {
    padding: '1rem',
    color: colors.text,
  },
  badge: {
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    display: 'inline-block',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    fontSize: '1.2rem',
    color: colors.textLight,
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: colors.textLight,
  },
  paginationContainer: {
    marginTop: '2rem',
  },
  paginationInfo: {
    textAlign: 'center' as const,
    color: colors.textLight,
    marginBottom: '1rem',
  },
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1002,
    padding: '1rem',
  },
  largeModal: {
    width: '95%',
    maxWidth: '1320px',
    maxHeight: '95vh',
    overflow: 'auto' as const,
    backgroundColor: colors.background,
    borderRadius: '8px',
  },
  entityCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.5rem',
    minWidth: 0,
  },
  inlineActionBtn: {
    backgroundColor: 'transparent',
    color: colors.textLight,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1.1rem',
    padding: '0.2rem 0.35rem',
    lineHeight: 1,
  },
  actionsCell: {
    textAlign: 'center' as const,
    position: 'relative' as const,
    minWidth: '80px',
  },
  dropdown: {
    position: 'relative' as const,
    display: 'inline-block',
  },
  dropdownMenu: {
    position: 'absolute' as const,
    top: '100%',
    right: 0,
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    zIndex: 1001,
    minWidth: '120px',
  },
  dropdownItem: {
    display: 'block',
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    textAlign: 'left' as const,
    cursor: 'pointer',
    fontSize: '14px',
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    fontFamily: 'inherit',
  },
  actionBtn: {
    padding: '8px 12px',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
  },
};

export default Opportunities;
